import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", phone: "", active: true };

export default function LabAgents() {
  const { can } = useAuth();
  const manage = can("lab.agents.manage");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/lab/agents", { params: { active: tab === "active" } }); setItems(data.items); }
    catch (e) { setError(apiError(e)); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setFormError(""); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Agent name is required."); return; }
    setBusy(true); setFormError("");
    try {
      if (editing) await api.patch(`/lab/agents/${editing.agent_id}`, form);
      else await api.post("/lab/agents", form);
      toast.success(editing ? "Agent updated." : "Agent added.");
      setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = async (a) => { try { await api.patch(`/lab/agents/${a.agent_id}`, { active: !a.active }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!can("lab.orders.view")) return <NoAccess what="Sample collection agents" />;

  return (
    <div data-testid="lab-agents-page">
      <PageHeader overline="Cases" title="Sample Collection Agents"
        subtitle="Field agents / phlebotomists who collect samples and can be assigned to cases."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-agent-btn"><Plus className="h-4 w-4" /> Add agent</button>} />
      <div className="flex gap-2 mb-5">
        {["active", "archived"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`agent-tab-${t}`}
            className={`px-4 py-2 rounded-full text-sm capitalize ${tab === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{t}</button>
        ))}
      </div>
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-agents-table" columns={["Agent ID", "Name", "Phone", "Registered", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No agents" subtitle="Add sample collection agents to assign to cases." testid="lab-agents-empty" />}
          renderRow={(a) => (
            <tr key={a.agent_id} data-testid={`lab-agent-row-${a.agent_id}`}>
              <Td className="font-mono text-slate-700">{a.agent_code}</Td>
              <Td className="font-medium text-slate-900">{a.name}</Td>
              <Td className="text-slate-600">{a.phone || "—"}</Td>
              <Td className="text-slate-500 text-xs">{(a.registered_on || "").slice(0, 10)}</Td>
              <Td><span className={`text-xs font-medium ${a.active ? "text-emerald-600" : "text-slate-400"}`}>{a.active ? "Active" : "Archived"}</span></Td>
              {manage && <Td>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(a)} className={ghostBtnCls} data-testid={`edit-agent-${a.agent_id}`}>Edit</button>
                  <button onClick={() => toggle(a)} className={ghostBtnCls} data-testid={`toggle-agent-${a.agent_id}`}>{a.active ? "Archive" : "Restore"}</button>
                </div>
              </Td>}
            </tr>
          )} />
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit agent" : "Add agent"} testid="lab-agent-modal">
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Agent name *"><input className={inputCls} data-testid="agent-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Phone"><input className={inputCls} data-testid="agent-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="agent-active" /> Active</label>
          {formError && <p className="text-sm text-red-600" data-testid="agent-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-agent-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
